// Derivations behind the redesigned task card (design handoff
// "TrakLog UI.dc.html", options 3a / 5c).
//
// Everything here is pure and index-stable: the card grid, the "Showing 7 of
// 12" count, the lightbox and the bulk-bar aggregate all read the same list in
// the same order. A grid that windowed its media differently from the viewer it
// opens would show the wrong frame on every click past the first gap, which is
// exactly the class of bug `hasRenderableMedia` was introduced to stop.

import FormFieldParser from './FormFieldParser';
import { formatDuration } from './taskFormatters';
import { hasRenderableMedia, isVideoItem, categoryKey } from './taskMedia';
import { taskLineItemsField } from './lineItems';
import { checkboxState } from './checkboxAnswer';
import { TRAK } from '../theme/trakTokens';
import { findLocale } from './locales';

/** Tiles the card shows before it collapses the tail into an overflow tile. */
export const MEDIA_TILE_LIMIT = 8;

export function toDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (typeof ts === 'object' && typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
  const d = ts instanceof Date ? ts : new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

function captureMillis(item) {
  const d = toDate(item?.timestamp || item?.createdAt);
  return d ? d.getTime() : null;
}

/**
 * Media in the order it was attached to the task, oldest first, with items that
 * have nothing to render dropped. Photos and videos interleave — a clip is a
 * category item like any photo and the sequence is the story of the job.
 *
 * Falls back to `sequence` when a pair has no usable time (older clients wrote
 * sequence only), and finally to the array order, so the sort is total and
 * stable rather than leaving equal items to the engine.
 *
 * Deliberately reads `timestamp`/`createdAt` and NOT `captured_at`, which is
 * where the tiles and the viewer now read their displayed time from (see
 * utils/photoProvenance.js). That disagreement between order and label is
 * intentional, not an oversight to tidy up: attach sequence is the narrative a
 * reviewer expects — first photo taken on site first — and an operator who
 * imports a month-old photo would otherwise move it to the front of an
 * evidence pack a supervisor may already have read in a different order. The
 * import is disclosed on the tile instead. If you change this, the grid, the
 * "Showing 7 of 12" count and the index the lightbox opens at all move
 * together, so change it deliberately.
 */
export function sortMediaByCaptureTime(images) {
  if (!Array.isArray(images)) return [];
  return images
    .filter(hasRenderableMedia)
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const ta = captureMillis(a.item);
      const tb = captureMillis(b.item);
      if (ta !== null && tb !== null && ta !== tb) return ta - tb;
      const sa = a.item?.sequence;
      const sb = b.item?.sequence;
      if (typeof sa === 'number' && typeof sb === 'number' && sa !== sb) return sa - sb;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

/**
 * Media grouped by photo category, each group in capture order, groups in the
 * order their first frame was taken.
 *
 * The one order every staff grid and the viewer behind it share: a grid
 * rendering one order while the lightbox indexes another opens the wrong frame
 * on every click. A task with a single category comes back unchanged.
 */
export function sortMediaByCategory(images) {
  const sorted = sortMediaByCaptureTime(images);
  const groups = new Map();
  sorted.forEach((item) => {
    const key = categoryKey(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  return [].concat(...groups.values());
}

/** True when a grid should print a group heading before `tiles[index]`. */
export function startsMediaGroup(tiles, index) {
  if (!Array.isArray(tiles) || index < 0 || index >= tiles.length) return false;
  return index === 0 || categoryKey(tiles[index]) !== categoryKey(tiles[index - 1]);
}

/**
 * Up to `limit` items from media already in `sortMediaByCategory` order,
 * spread across its categories: one from each group in turn, until the limit.
 * The picks keep the input order, so a short strip reads as the start of the
 * full grid with some frames skipped, never reordered. With one category it
 * is simply the first `limit` items.
 */
export function sampleAcrossMediaGroups(sortedMedia, limit) {
  if (!Array.isArray(sortedMedia) || limit <= 0) return [];
  if (sortedMedia.length <= limit) return sortedMedia.slice();
  const groups = [];
  sortedMedia.forEach((item, index) => {
    if (startsMediaGroup(sortedMedia, index)) groups.push([]);
    groups[groups.length - 1].push(index);
  });
  const picked = new Set();
  for (let round = 0; picked.size < limit; round += 1) {
    groups.forEach((indexes) => {
      if (picked.size < limit && round < indexes.length) picked.add(indexes[round]);
    });
  }
  return sortedMedia.filter((_, index) => picked.has(index));
}

/** More than one category among these items — below that, no headings. */
export function hasSeveralMediaGroups(items) {
  if (!Array.isArray(items) || items.length === 0) return false;
  const first = categoryKey(items[0]);
  return items.some((item) => categoryKey(item) !== first);
}

/**
 * The 8-then-overflow rule: up to 8 items show in full (two rows of four); from
 * 9 up the first 7 show and the 8th tile becomes the 8th thumbnail under a
 * scrim reading "+N more".
 *
 * `overflowIndex` is the index the lightbox opens at when that tile is clicked,
 * so the reviewer lands on the first frame the card could not show.
 */
export function mediaWindow(sortedMedia) {
  const media = Array.isArray(sortedMedia) ? sortedMedia : [];
  if (media.length <= MEDIA_TILE_LIMIT) {
    return { tiles: media, overflowItem: null, overflowCount: 0, overflowIndex: null, total: media.length };
  }
  return {
    tiles: media.slice(0, MEDIA_TILE_LIMIT - 1),
    overflowItem: media[MEDIA_TILE_LIMIT - 1],
    overflowCount: media.length - (MEDIA_TILE_LIMIT - 1),
    overflowIndex: MEDIA_TILE_LIMIT - 1,
    total: media.length,
  };
}

/**
 * Every item, in one grid — what the card shows once the reviewer opens the
 * overflow tile. Same shape as `mediaWindow` so the grid and the lightbox
 * still read one window, with nothing held back.
 */
export function expandedWindow(sortedMedia) {
  const media = Array.isArray(sortedMedia) ? sortedMedia : [];
  return { tiles: media, overflowItem: null, overflowCount: 0, overflowIndex: null, total: media.length };
}

/** Muted line at the left of the action row. */
export function mediaSummary(sortedMedia, { expanded = false, expectedCount = 0 } = {}) {
  const media = Array.isArray(sortedMedia) ? sortedMedia : [];
  const total = media.length;
  const expected = Number(expectedCount) || 0;

  // Still arriving: say how far along, not how many have landed. A task
  // mid-upload reading "2 photos" is indistinguishable from one that only ever
  // had two, which is the difference between "wait" and "this is all there is".
  // The breakdown isn't known until they arrive, so this counts media, not
  // photos specifically.
  if (expected > total) return `Photos: ${total}/${expected}`;

  const { overflowCount } = mediaWindow(media);
  // "Showing 7 of 23" is a statement about the window, so it stops being true
  // the moment the reviewer expands it.
  if (overflowCount > 0 && !expanded) return `Showing ${MEDIA_TILE_LIMIT - 1} of ${total}`;

  // No ", all shown" tail anywhere: it existed to contrast with the
  // "Showing 7 of 12" case above, but that case has already returned by here,
  // so every remaining card is showing everything it has. Saying so on a card
  // holding two photos is noise the reviewer has to read past on every row.
  // Labelled and explicit, including the zero. "No photos yet" read as a
  // status rather than a count, so a task with none looked like one whose
  // photos had not arrived — and a reviewer scanning a column of cards had no
  // single place the number always is.
  const videos = media.filter(isVideoItem).length;
  const photos = total - videos;
  const parts = [];
  if (photos > 0 || videos === 0) parts.push(`Photos: ${photos}`);
  if (videos > 0) parts.push(`Videos: ${videos}`);
  return parts.join(' · ');
}

/**
 * Label for the ZIP row in the Export menu.
 *
 * The archive has always carried whatever the task holds — the API stores mp4s
 * uncompressed alongside the photos — but the row said "ZIP of photos"
 * regardless, so a task of three clips offered an export that read as though
 * it would not contain them.
 */
export function zipExportLabel(sortedMedia) {
  const media = Array.isArray(sortedMedia) ? sortedMedia : [];
  if (media.length === 0) return 'ZIP of photos';
  const videos = media.filter(isVideoItem).length;
  if (videos === media.length) return 'ZIP of videos';
  if (videos > 0) return 'ZIP of photos & videos';
  return 'ZIP of photos';
}

/** "1 photo · 1 clip" — names both rather than flattening them to "2 items". */
function mixedMediaLabel(photos, videos) {
  const photoPart = `${photos} ${photos === 1 ? 'photo' : 'photos'}`;
  const videoPart = `${videos} ${videos === 1 ? 'clip' : 'clips'}`;
  return `${photoPart} · ${videoPart}`;
}

/**
 * Status pill: dot + label, coloured by what the supervisor should do about it.
 * An uploading task carries its progress in the label ("Uploading 3/8") because
 * that is the number they are waiting on.
 */
export function statusPresentation(task) {
  const status = (task?.status || '').toLowerCase();

  if (status === 'uploading') {
    const done = task?.imagesUploadedCount || 0;
    const total = task?.imageCount || 0;
    return {
      label: total ? `Uploading ${done}/${total}` : 'Uploading',
      bg: TRAK.warnFill,
      fg: TRAK.warnText,
      dot: TRAK.warnDot,
    };
  }

  if (status === 'rejected') {
    return { label: 'Rejected', bg: TRAK.dangerFill, fg: TRAK.dangerText, dot: TRAK.danger };
  }

  if (status === 'approved') {
    return { label: 'Approved', bg: TRAK.successFill, fg: TRAK.greenDark, dot: TRAK.green };
  }

  if (status === 'submitted') {
    return { label: 'Submitted', bg: TRAK.successFill, fg: TRAK.greenDark, dot: TRAK.green };
  }

  if (!status || status === 'uploaded') {
    return { label: 'Uploaded', bg: TRAK.successFill, fg: TRAK.greenDark, dot: TRAK.green };
  }

  // draft / assigned / in_progress / in_review and anything a later release
  // adds: neutral rather than a colour that implies an outcome.
  const label = status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  return { label, bg: TRAK.fill, fg: TRAK.muted, dot: 'rgba(13,51,32,.35)' };
}

/** Bytes as the card and the export menu spell them: "2 MB", "912 KB". */
export function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  // One decimal below 10 ("1.5 MB"), whole numbers above — and never a "2.0 MB"
  // that reads as a measurement the app didn't make.
  const rounded = value >= 10 || i === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[i]}`;
}

/** Stored byte size of a task's media, across the field spellings in use. */
export function taskByteSize(task) {
  return task?.totalImageSize || task?.totalSize || task?.size || 0;
}

/** "5 Aug 2026, 09:01:44" — the card's timestamp register. */
export function formatCardTimestamp(ts) {
  const date = toDate(ts);
  if (!date) return null;
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  return `${day}, ${time}`;
}

/**
 * "Thu 20 Aug 2026 11:54" — the card's started→submitted line.
 *
 * The card used to carry two full timestamps in adjacent cells to express a
 * span that is usually seconds long, repeating the date verbatim. This states
 * the span once and collapses whatever is redundant in it:
 *
 *   same minute   Thu 20 Aug 2026 11:54
 *   same day      Thu 20 Aug 2026 11:54 - 14:20
 *   across days   Thu 20 Aug 2026 11:54 - Fri 21 Aug 2026 01:04
 *
 * Minutes, not seconds. The exact second is still on the task detail view and
 * on every timeline entry, so the card can be the summary and the timeline the
 * record. The consequence is a boundary artifact worth knowing about: 11:54:59
 * to 11:55:01 is two seconds but renders as a range, while 11:54:01 to 11:54:59
 * is fifty-eight and renders as a point. The duration shown beneath resolves
 * it, which is why that line stays.
 *
 * The year is never dropped. Migrated legacy tasks are genuinely years old and
 * a bare "Thu 20 Aug" on one of them reads as this August.
 *
 * @returns {string|null} null when there is no start to show
 */
export function formatCardTimeRange(started, submitted) {
  const parts = formatCardTimeRangeParts(started, submitted);
  return parts ? parts.map(p => p.text).join('') : null;
}

/**
 * The same range as `formatCardTimeRange`, in pieces, with exactly one date
 * marked `bold`: the date the task was submitted. A task done within a day
 * shows one date and that is it; one that crossed midnight shows two, and the
 * submission date is the one a reader filing by day is looking for. With no
 * submission yet, the start date is the only one there is.
 *
 * @returns {{text: string, bold: boolean}[]|null}
 */
export function formatCardTimeRangeParts(started, submitted) {
  const from = toDate(started);
  const to = toDate(submitted);
  if (!from) return to ? stampParts(to, true) : null;

  // No submission yet (draft, still uploading): the start alone, no dangling
  // dash promising a second half that isn't coming.
  if (!to) return stampParts(from, true);

  // Submitted before started. Not hypothetical: mobile is offline-first and
  // these timestamps can come off a device clock, so a skewed pair must not
  // render as a backwards range. Both stamps in full, and the reader can see
  // for themselves that something is off.
  if (to.getTime() < from.getTime()) {
    return [...stampParts(from, false), plain(' - '), ...stampParts(to, true)];
  }

  if (sameMinute(from, to)) return stampParts(from, true);
  if (sameDay(from, to)) return [...stampParts(from, true), plain(` - ${formatClockTime(to)}`)];
  return [...stampParts(from, false), plain(' - '), ...stampParts(to, true)];
}

function plain(text) {
  return { text, bold: false };
}

/** "Thu 20 Aug 2026" + " 11:54": one end of a range, date and time apart. */
function stampParts(date, boldDate) {
  const day = date.toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  }).replace(/,/g, '');
  return [{ text: day, bold: boldDate }, plain(` ${formatClockTime(date)}`)];
}

/** "11:54" — hours and minutes, 24h. */
function formatClockTime(date) {
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function sameMinute(a, b) {
  return sameDay(a, b) && a.getHours() === b.getHours() && a.getMinutes() === b.getMinutes();
}

/** "09:01" for a media tile badge. */
export function formatTileTime(ts) {
  const date = toDate(ts);
  if (!date) return null;
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * Started → submitted, as the card's "· 11s ·" segment.
 *
 * The two stamps win over the stored `duration` whenever both are present,
 * because this label sits directly beside the range they render and must not
 * contradict it. Mobile computes `duration` as exactly this difference, so
 * that costs nothing there — but the legacy migration measured to the
 * *approval* instead, so a two-minute checklist approved three days later
 * read "3 days" next to "07:51 - 07:53". `duration` still covers a task that
 * carries only one of the two stamps.
 */
export function taskDurationLabel(task) {
  const started = toDate(task?.createdAt || task?.created_at);
  const submitted = toDate(task?.submittedAt || task?.uploadedAt || task?.uploadCompletedAt);
  if (started && submitted) {
    const seconds = Math.round((submitted - started) / 1000);
    return seconds > 0 ? formatDuration(seconds) : null;
  }
  if (typeof task?.duration === 'number' && task.duration > 0) return formatDuration(task.duration);
  return null;
}

export function taskStartedAt(task) {
  return task?.createdAt || task?.created_at || null;
}

export function taskSubmittedAt(task) {
  return task?.submittedAt || task?.uploadedAt || task?.uploadCompletedAt || null;
}

/**
 * The collected-data box: label/value rows in the task's own order plus the count of
 * required fields the operator left blank, which the header strip reports as
 * "N fields empty".
 *
 * `isRequired` comes from the form template — FormFieldParser hardcodes it to
 * false because a submitted task doesn't carry the requirement, only the value.
 * `checked` is a checkbox field's answer (true / false), drawn as a tick or a
 * cross; null for every other field.
 */
export function collectedData(task, templateFields) {
  // The task describes itself: its own labels (in the template's source
  // language), types and order (FormFieldParser / taskFieldSnapshot). The
  // template below only adds what the task does not carry: which fields are
  // required, which one is the task identifier, a line-items field's noun.
  const parsed = FormFieldParser.parseTaskFormData(task, templateFields);
  if (!parsed || !parsed.fields || parsed.fields.length === 0) return null;

  const requiredIds = new Set(
    (Array.isArray(templateFields) ? templateFields : [])
      .filter(f => f?.required === true || f?.is_required === true)
      .map(f => f?.id || f?.field_id)
      .filter(Boolean)
  );

  // The field a template marks as its task identifier is already the card's
  // headline — repeating it as the first row of the table says the same thing
  // twice, in the same eyeline, for every task. Dropped only when it has a
  // value: an identifier the operator left blank is worth showing, and is what
  // the "N fields empty" count is for.
  const identifierIds = new Set(
    (Array.isArray(templateFields) ? templateFields : [])
      .filter(f => f?.isTaskIdentifier === true || f?.is_task_identifier === true)
      .map(f => f?.id || f?.field_id)
      .filter(Boolean)
  );

  const rows = parsed.fields
    .filter(field => !(identifierIds.has(field.id) && !field.isEmpty))
    .map(field => {
      // A line-items field's `formData` value is only ever the summary
      // string (docs/line-items.md "Design principles" #1) — never an
      // object — so `String(field.value)` is always safe here. When the
      // task also carries the structured rows, readers that know about them
      // (TaskCollectedData, CustomerJobCard) render the real table instead
      // of the summary; readers that don't (or a task with no rows at all,
      // e.g. an older phone that typed plain text) fall back to `value`.
      const lineItems = taskLineItemsField(task, field.id);
      // `item_label` ("flavour") is a template setting, not part of the
      // task's own line_items snapshot (contract: the snapshot is just
      // {columns, rows}) — read it off the live template field so "3
      // flavours" survives even though the task document never stored the
      // word. A deleted field or template falls back to "line" downstream.
      const templateField = (Array.isArray(templateFields) ? templateFields : [])
        .find(f => (f?.id || f?.field_id) === field.id);
      return {
        id: field.id,
        label: field.name,
        value: field.isEmpty ? null : String(field.value),
        checked: field.isEmpty ? null : checkboxAnswer(field, templateField),
        isEmpty: field.isEmpty,
        isRequired: requiredIds.has(field.id),
        lineItems: (lineItems && lineItems.columns.length > 0)
          ? { ...lineItems, itemLabel: templateField?.line_items?.item_label || null }
          : null,
      };
    });

  return {
    templateName: parsed.templateName,
    rows,
    emptyRequiredCount: rows.filter(r => r.isEmpty && r.isRequired).length,
  };
}

// A checkbox field's answer as true / false (null for any other field, or a
// value that is not a yes / no). The views draw it as a tick or a cross. The
// template's type is checked as well as the header's, because an API-created
// task carries no headers to read a type from.
function checkboxAnswer(field, templateField) {
  const isCheckbox = field.type === 'checkbox'
    || (templateField?.type || templateField?.field_type) === 'checkbox';
  return isCheckbox ? checkboxState(field.value) : null;
}

/**
 * The language the operator read the form in, when it was a translation of
 * the template (`capture_locale`), as "Thai"; null for the template's own
 * language. The task's labels are always shown in the template's language;
 * this is the note that says the operator saw another one.
 */
export function captureLanguageLabel(task) {
  const code = task?.capture_locale;
  if (typeof code !== 'string' || !code.trim()) return null;
  const tag = code.trim();
  const hit = findLocale(tag) || findLocale(tag.split('-')[0]);
  return hit ? hit.label : tag;
}

/**
 * The name to show for a task's template: the template's own current
 * `template_name` when the template still exists, otherwise the name stored on
 * the task. The stored name is a snapshot from capture time; the live template
 * is what the collected-data labels are read from, so the two must agree.
 *
 * @param {Object} task
 * @param {Function} [getTemplateNameById] - TemplateContext lookup
 * @returns {string|null}
 */
export function resolveTaskTemplateName(task, getTemplateNameById) {
  const templateId = task?.templateId || task?.template_id || null;
  const live = templateId && typeof getTemplateNameById === 'function'
    ? getTemplateNameById(templateId)
    : null;
  return live || task?.templateName || task?.template_name || null;
}

/** "4 fields · 8 photos · 3.5 MB" — the line under the task id. Size rides
 *  with the media count it describes rather than with the duration. */
export function templateSummaryLine(templateInfo, mediaCount, videoCount = 0, bytes = 0) {
  const parts = [];
  const fieldCount = templateInfo?.summary?.fieldCount;
  if (typeof fieldCount === 'number' && fieldCount > 0) {
    parts.push(`${fieldCount} ${fieldCount === 1 ? 'field' : 'fields'}`);
  }
  if (mediaCount > 0) {
    if (videoCount === mediaCount) {
      parts.push(`${mediaCount} ${mediaCount === 1 ? 'clip' : 'clips'}`);
    } else if (videoCount > 0) {
      parts.push(mixedMediaLabel(mediaCount - videoCount, videoCount));
    } else {
      parts.push(`${mediaCount} ${mediaCount === 1 ? 'photo' : 'photos'}`);
    }
  }
  // Size trails the count it describes. It survives an empty media array
  // because a task can report bytes before its images have loaded.
  if (bytes > 0) parts.push(formatBytes(bytes));
  return parts.join(' · ');
}

/** Aggregate shown in the docked selection bar: "20 photos · 11 MB". */
export function selectionAggregate(tasks) {
  const list = Array.isArray(tasks) ? tasks : [];
  const media = list.reduce(
    (sum, t) => sum + (Array.isArray(t?.images) ? t.images.filter(hasRenderableMedia).length : (t?.imageCount || 0)),
    0
  );
  const bytes = list.reduce((sum, t) => sum + taskByteSize(t), 0);
  const parts = [];
  if (media > 0) parts.push(`${media} ${media === 1 ? 'photo' : 'photos'}`);
  if (bytes > 0) parts.push(formatBytes(bytes));
  return parts.join(' · ');
}
